import React, { useState } from 'react';
import axios from 'axios';
import styles from './ComposeEmailModal.module.css';
import { URL as BASEURL } from '../AxiosUrlGlobal';

/**
 * ComposeEmailModal - Compose and send new email
 */
function ComposeEmailModal({ accounts, onClose, onSent, userId, token }) {
    const [formData, setFormData] = useState({
        from: accounts.length > 0 ? accounts[0].email : '',
        to: '',
        cc: '',
        subject: '',
        text: '',
        html: ''
    });
    const [sending, setSending] = useState(false);

    const handleSubmit = async () => {
        if (!formData.from || !formData.to || !formData.subject || !formData.text.trim()) {
            alert('Please fill in all required fields (From, To, Subject, Message)');
            return;
        }

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.to)) {
            alert('Please enter a valid email address');
            return;
        }

        try {
            setSending(true);

            const emailPayload = {
                from: formData.from,
                to: formData.to,
                cc: formData.cc || undefined,
                subject: formData.subject,
                text: formData.text,
                html: formData.html || formData.text.replace(/\n/g, '<br>')
            };

            const response = await axios.post(
                `${BASEURL}user/email/send/${userId}`,
                emailPayload,
                { headers: { token } }
            );

            if (response.data.success) {
                alert('Email sent successfully!');
                if (onSent) {
                    onSent();
                }
                onClose();
            } else {
                alert(response.data.error || 'Failed to send email');
            }
        } catch (err) {
            console.error('Error sending email:', err);
            alert(err.response?.data?.error || 'Failed to send email');
        } finally {
            setSending(false);
        }
    };

    return (
        <div className={styles.modalOverlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <h2>Compose Email</h2>
                    <button className={styles.closeButton} onClick={onClose}>&times;</button>
                </div>

                <div className={styles.modalContent}>
                    <div className={styles.formGroup}>
                        <label>From: *</label>
                        <select
                            value={formData.from}
                            onChange={(e) => setFormData({ ...formData, from: e.target.value })}
                            required
                        >
                            {accounts.map((account) => (
                                <option key={account._id} value={account.email}>
                                    {account.displayName ? `${account.displayName} <${account.email}>` : account.email}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.formGroup}>
                        <label>To: *</label>
                        <input
                            type="email"
                            value={formData.to}
                            onChange={(e) => setFormData({ ...formData, to: e.target.value })}
                            placeholder="recipient@example.com"
                            required
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label>CC:</label>
                        <input
                            type="email"
                            value={formData.cc}
                            onChange={(e) => setFormData({ ...formData, cc: e.target.value })}
                            placeholder="cc@example.com (optional)"
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label>Subject: *</label>
                        <input
                            type="text"
                            value={formData.subject}
                            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                            placeholder="Email subject"
                            required
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label>Message: *</label>
                        <textarea
                            value={formData.text}
                            onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                            rows="12"
                            required
                            placeholder="Type your message here..."
                            className={styles.messageTextarea}
                        />
                    </div>
                </div>

                <div className={styles.modalFooter}>
                    <button
                        className={styles.cancelButton}
                        onClick={onClose}
                        disabled={sending}
                    >
                        Cancel
                    </button>
                    <button
                        className={styles.sendButton}
                        onClick={handleSubmit}
                        disabled={sending}
                    >
                        {sending ? 'Sending...' : 'Send Email'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default ComposeEmailModal;
